import { ModalV2 } from "#src/commonV2/modal/ModalV2";
import { useTranslation } from "react-i18next";
import {
  controlDirectionFilterItems,
  ipActionFilterItems,
} from "../filterItems";
import { IFilterItemV2 } from "#src/commonV2/Table/Filter/types";
import { useEffect, useState } from "react";
import { FormSelectV2 } from "#src/commonV2/form/Select";
import { FormInputMultipleV2 } from "#src/commonV2/form/InputMultiple";
import patterns from "#src/config/patterns";
import { FormTextArea } from "#src/commonV2/form/TextArea";
import { ButtonV2 } from "#src/commonV2/Button";
import { endpointService } from "#src/services/endpoint";
import { FormSelectMultipleV2 } from "#src/commonV2/form/SelectMultiple";
import { getOsIcon } from "../../utils";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { FormTags } from "#src/commonV2/form/Tags";
import { IThreatPreventionPolicyItem } from "#src/services/endpoint/devices/types";
import { DeviceTagType } from "#src/services/endpoint/enums";
import { useQueryDevice } from "../useQueryDevice";
import { useQueryTag } from "../useQueryTag";

interface IEditIpPolicyModalProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  updatePolicyList: () => void;
  editingItem: IThreatPreventionPolicyItem | null;
}

export const EditIpPolicyModal = ({
  open,
  onClose,
  workspaceId,
  updatePolicyList,
  editingItem,
}: IEditIpPolicyModalProps) => {
  const { t } = useTranslation("endpoint", {
    keyPrefix: "threatPrevention.modal.addAndEdit",
  });

  // --------------- DATA ---------------

  const [action, setAction] = useState<IFilterItemV2>(ipActionFilterItems[0]);
  const [selectedDirections, setSelectedDirections] = useState<IFilterItemV2[]>(
    []
  );
  const [directionKeyword, setDirectionKeyword] = useState<string>("");
  const [matches, setMatches] = useState<string[]>([]);
  const [matchesInput, setMatchesInput] = useState<string>("");
  const [selectedTags, setSelectedTags] = useState<IFilterItemV2[]>([]);
  const [tagKeyword, setTagKeyword] = useState<string>("");
  const [selectedDevices, setSelectedDevices] = useState<IFilterItemV2[]>([]);
  const [deviceKeyword, setDeviceKeyword] = useState<string>("");
  const [note, setNote] = useState<string>("");

  // --------------- HOOKS ---------------

  const tagInitList = useQueryTag(workspaceId, "");
  const deviceInitList = useQueryDevice(workspaceId, "");

  const tagList = useQueryTag(workspaceId, tagKeyword);
  const deviceList = useQueryDevice(workspaceId, deviceKeyword);

  // --------------- METHODS ---------------

  const onCloseAndCleanup = () => {
    setAction(ipActionFilterItems[0]);
    setSelectedDirections([]);
    setDirectionKeyword("");
    setMatches([]);
    setMatchesInput("");
    setSelectedTags([]);
    setTagKeyword("");
    setSelectedDevices([]);
    setDeviceKeyword("");
    setNote("");
    onClose();
  };

  const onSubmitPolicy = () => {
    if (editingItem) {
      endpointService
        .updateThreatPolicy(workspaceId, editingItem.id, {
          group: "network",
          action: action.value ?? "",
          sources: selectedDirections
            .map((item) => item.value)
            .filter((item): item is string => typeof item !== "undefined"),
          description: note,
          matches: matches,
          hosts_tags: [
            ...selectedTags.map((tag) => ({
              id: tag.value ?? "",
              type: DeviceTagType.TAG,
            })),
            ...selectedDevices.map((device) => ({
              id: device.value ?? "",
              type: DeviceTagType.HOST,
            })),
          ],
        })
        .then(() => {
          updatePolicyList();
          onCloseAndCleanup();
        })
        .catch(apiErrorHandler);
    }
  };

  // --------------- EFFECTS ---------------

  useEffect(() => {
    if (editingItem && tagInitList && deviceInitList) {
      const findAction = ipActionFilterItems.find(
        (item) => item.value === editingItem.action
      );
      if (findAction) {
        setAction(findAction);
      }
      setMatches(editingItem.matches);

      const findDirections = controlDirectionFilterItems.filter(
        (item) => item.value && editingItem.sources.includes(item.value)
      );
      if (findDirections) {
        setSelectedDirections(findDirections);
      }

      const findTags = tagInitList.filter((tag) =>
        editingItem.hosts_tags.map((item) => item.id).includes(tag.id)
      );
      setSelectedTags(
        findTags.map((tag) => ({
          key: tag.id.toString(),
          value: tag.id.toString(),
          getLabel: () => tag.name,
        }))
      );

      const findDevices = deviceInitList.filter((device) =>
        editingItem.hosts_tags.map((item) => item.id).includes(device.id)
      );
      setSelectedDevices(
        findDevices.map((device) => ({
          key: device.id.toString(),
          value: device.id.toString(),
          getLabel: () => device.name,
          leftIcon:
            device.os && device.os.family
              ? getOsIcon(device.os.family, 1)
              : null,
        }))
      );

      setNote(editingItem.description);
    }
  }, [editingItem, tagInitList, deviceInitList]);

  // --------------- RENDER ---------------

  return (
    <ModalV2
      open={open}
      onChangeOpen={() => {
        onCloseAndCleanup();
      }}
      className="w-[48rem]"
    >
      <span className="text-lg font-semibold text-gray-v2-900">
        {t("title.ips.add")}
      </span>
      <div className="grid grid-cols-2 gap-6">
        <FormSelectV2
          label={t("action.label")}
          selected={action}
          onChangeSelected={(value) => {
            setAction(value);
          }}
          options={ipActionFilterItems}
          requiredMark
          disabled
        />
        <FormTags
          label={t("directions.label")}
          values={selectedDirections}
          onChangeValues={(newValues) => {
            setSelectedDirections(newValues);
          }}
          keyword={directionKeyword}
          onChangeKeyword={(newKeyword) => {
            setDirectionKeyword(newKeyword);
          }}
          options={controlDirectionFilterItems.filter(
            (item) => item.value && item.value.includes(directionKeyword)
          )}
          requiredMark
          placeholder={t("directions.placeholder")}
          disabled
        />
        <div className="col-span-2">
          <FormInputMultipleV2
            label={t("ipCidr.label")}
            values={matches}
            onChangeValues={(newValues) => {
              setMatches(newValues);
            }}
            input={matchesInput}
            onChangeInput={(newInput) => {
              setMatchesInput(newInput);
            }}
            required
            placeholder={t("ipCidr.placeholder")}
            hint={t("ipCidr.hint")}
            validation={{
              pattern: {
                value: patterns.IP,
                errorMessage: () => t("ipCidr.notification.invalid.message"),
              },
            }}
            column={2}
          />
        </div>
        <FormTags
          label={t("applyToTags.label")}
          values={selectedTags}
          onChangeValues={(newValues) => {
            setSelectedTags(newValues);
          }}
          keyword={tagKeyword}
          onChangeKeyword={(newKeyword) => {
            setTagKeyword(newKeyword);
          }}
          options={
            tagList
              ? tagList.map((item) => ({
                  key: item.id.toString(),
                  value: item.id.toString(),
                  getLabel: () => item.name,
                }))
              : []
          }
          placeholder={t("applyToTags.placeholder")}
        />
        <FormSelectMultipleV2
          label={t("applyToDevices.label")}
          values={selectedDevices}
          onChangeValues={(newValues) => {
            setSelectedDevices(newValues);
          }}
          keyword={deviceKeyword}
          onChangeKeyword={(newKeyword) => {
            setDeviceKeyword(newKeyword);
          }}
          options={
            deviceList
              ? deviceList.map((item) => ({
                  key: item.id.toString(),
                  value: item.id.toString(),
                  getLabel: () => item.name,
                  leftIcon:
                    item.os && item.os.family
                      ? getOsIcon(item.os.family, 1)
                      : null,
                }))
              : []
          }
          placeholder={t("applyToDevices.placeholder")}
          column={1}
        />
        <div className="col-span-2">
          <FormTextArea
            label={t("notes.label")}
            value={note}
            onChangeValue={(newValue) => {
              setNote(newValue);
            }}
            placeholder={t("notes.placeholder")}
            rows={2}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <ButtonV2
          variant="secondary"
          color="gray"
          onClick={() => {
            onCloseAndCleanup();
          }}
        >
          {t("button.cancel")}
        </ButtonV2>
        <ButtonV2 onClick={onSubmitPolicy}>{t("button.submit")}</ButtonV2>
      </div>
    </ModalV2>
  );
};
